/**
 * Color utilities used by ThemeProvider to apply per-tenant CSS variables.
 * All functions are pure — no side-effects.
 */

/**
 * Convert a CSS hex color string to space-separated RGB components.
 * Output format matches what Tailwind requires for opacity support:
 *   `rgb(var(--color-primary) / 0.5)` needs `--color-primary: "59 130 246"`
 *
 * Supports 3-char (#RGB) and 6-char (#RRGGBB) hex.
 */
export function hexToRgbComponents(hex: string): string {
  const cleaned = hex.replace(/^#/, '')
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned

  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    console.warn(`[colorUtils] Invalid hex color: "${hex}", falling back to #2563EB`)
    return '37 99 235'
  }

  return `${r} ${g} ${b}`
}

/**
 * Compute WCAG 2.1 relative luminance for a hex color.
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function relativeLuminance(hex: string): number {
  const cleaned = hex.replace(/^#/, '')
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned

  const toLinear = (channel: number): number => {
    const sRGB = channel / 255
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4)
  }

  const r = toLinear(parseInt(full.slice(0, 2), 16))
  const g = toLinear(parseInt(full.slice(2, 4), 16))
  const b = toLinear(parseInt(full.slice(4, 6), 16))

  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Return a foreground hex color (#FFFFFF or #111827) that has sufficient
 * contrast ratio against the given background hex color.
 * Uses WCAG 2.1 luminance threshold.
 */
export function getContrastForeground(backgroundHex: string): string {
  const luminance = relativeLuminance(backgroundHex)
  // Threshold: luminance > 0.179 means the background is light enough to need dark text
  return luminance > 0.179 ? '#111827' : '#ffffff'
}

/**
 * Darken a hex color by reducing its lightness in HSL space.
 * Used to derive a hover variant from the primary brand color.
 */
export function darkenHex(hex: string, amount = 10): string {
  const cleaned = hex.replace(/^#/, '')
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned

  const r = parseInt(full.slice(0, 2), 16) / 255
  const g = parseInt(full.slice(2, 4), 16) / 255
  const b = parseInt(full.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }

  l = Math.max(0, l - amount / 100)

  // HSL to RGB
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }

  let rOut: number, gOut: number, bOut: number
  if (s === 0) {
    rOut = gOut = bOut = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    rOut = hue2rgb(p, q, h + 1 / 3)
    gOut = hue2rgb(p, q, h)
    bOut = hue2rgb(p, q, h - 1 / 3)
  }

  const toHex = (x: number): string =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0')

  return `#${toHex(rOut)}${toHex(gOut)}${toHex(bOut)}`
}

/** RTL locales — used by ThemeProvider to derive text direction */
export const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'ku', 'sd', 'yi'])

/** Return 'rtl' for right-to-left locales, else 'ltr' */
export function getDirection(locale: string): 'ltr' | 'rtl' {
  const lang = locale.split('-')[0].toLowerCase()
  return RTL_LOCALES.has(lang) ? 'rtl' : 'ltr'
}
